import {
  CheckCircle,
  Edit,
  Loader2,
  Plus,
  Search,
  Trash2,
  X,
} from "lucide-react";
import { useParams } from "react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import useAxios from "../../../../../../hooks/useAxios";
import useToastWrapper from "../../../../../../hooks/useToastWrapper";
import AddDimensionDialog from "./AddDimensionDialog";
import DeleteDimensionDialog from "./DeleteDimensionDialog";
import { TabsContent } from "../../../../../../shared/components/tabs/Tabs";
import { Input } from "../../../../../../shared/components/input/Input";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../../../../../shared/components/card/Card";
import UnexpectedBackendError from "../../../../../../shared/components/UnexpectedBackendError";
import { Label } from "../../../../../../shared/components/label/Label";
import OptionsDropdown from "./OptionsDropdown";
import { Textarea } from "../../../../../../shared/components/textarea/Textarea";
import { Button } from "../../../../../../shared/components/button/Button";
import {
  API_DATASETS_DIMENSION_BY_BOTH_ID,
  API_DATASETS_DIMENSION_BY_ID,
  API_DATASETS_DIMENSION_TYPES,
  API_DATASETS_DIMENSIONS,
} from "../../../../../../shared/lib/apiEndpointsResolver";

const DimensionsTabContent = ({ setDimensionsLength }) => {
  const { id } = useParams();
  const axios = useAxios();
  const { t } = useTranslation();
  const { display, displayError, displayUnexpectedError } = useToastWrapper();
  const [dimensions, setDimensions] = useState([]);
  const [dimensionTypes, setDimensionTypes] = useState([]);
  const [isDimensionDialogOpen, setIsDimensionDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editingData, setEditingData] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [dimensionToDelete, setDimensionToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [unexpectedError, setUnexpectedError] = useState(false);
  const [dimensionFilter, setDimensionFilter] = useState("");

  const openDimensionDialog = () => {
    setIsDimensionDialogOpen(true);
  };

  const onAddDimensionSuccessfully = (newDimension) => {
    setDimensions((prev) => [newDimension, ...prev]);
    setDimensionsLength(dimensions.length + 1);
    display(
      t("datasetsCentre.common.success"),
      t("datasetsCentre.datasets.dimensions.addSuccess", {
        code: newDimension.code,
      }),
    );
  };

  const checkDimensionCodeDuplicated = (code) => {
    return dimensions.some((d) => d.code === code.trim());
  };

  const checkDimensionColumnNameDuplicated = (columnName) => {
    return dimensions.some((d) => d.columnName === columnName.trim());
  };

  const validateDimensionForm = (form, excludeId) => {
    const errors = {};

    if (!form.code.trim()) {
      errors.code = t("datasetsCentre.common.codeRequired");
    } else if (
      dimensions.some((d) => d.code === form.code.trim() && d.id !== excludeId)
    ) {
      errors.code = t("datasetsCentre.common.codeExists");
    }

    if (!form.columnName.trim()) {
      errors.columnName = t("datasetsCentre.common.columnNameRequired");
    } else if (
      dimensions.some(
        (d) => d.columnName === form.columnName.trim() && d.id !== excludeId,
      )
    ) {
      errors.columnName = t("datasetsCentre.common.columnNameExists");
    }

    if (!form.name.en.trim()) {
      errors.nameEn = t("datasetsCentre.common.nameEnRequired");
    }

    if (!form.name.es.trim()) {
      errors.nameEs = t("datasetsCentre.common.nameEsRequired");
    }

    if (!form.description.en.trim()) {
      errors.descriptionEn = t("datasetsCentre.common.descriptionEnRequired");
    }

    if (!form.description.es.trim()) {
      errors.descriptionEs = t("datasetsCentre.common.descriptionEsRequired");
    }

    return errors;
  };

  const startEditingDimension = (dimension) => {
    setEditingId(dimension.id);
    setEditingData({ ...dimension });
  };

  const cancelEditingDimension = () => {
    setEditingId(null);
    setEditingData(null);
    setFormErrors({});
  };

  const saveEditingDimension = async () => {
    if (!editingData) return;

    const errors = validateDimensionForm(editingData, editingId);
    setFormErrors(errors);

    if (Object.keys(errors).length > 0) {
      return;
    }

    setIsSubmitting(true);

    try {
      const result = await axios.put(
        API_DATASETS_DIMENSION_BY_BOTH_ID(id, editingId),
        editingData,
      );

      if (result.status === 200) {
        const updatedDimension = result.data;
        setDimensions((prev) =>
          prev.map((dim) => (dim.id === editingId ? updatedDimension : dim)),
        );
        setEditingId(null);
        setEditingData(null);
        setFormErrors({});
        display(
          t("datasetsCentre.common.success"),
          t("datasetsCentre.datasets.dimensions.updateSuccess", {
            code: updatedDimension.code,
          }),
        );
      } else {
        console.error(result.data);
        setUnexpectedError(true);
      }
    } catch (error) {
      console.error(error);
      setUnexpectedError(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const displayDeleteDimensionDialog = (dimension) => {
    setDimensionToDelete(dimension);
  };

  const cancelDeleteDimension = () => {
    setDimensionToDelete(null);
  };

  const deleteDimension = async (id) => {
    setIsDeleting(true);

    try {
      const result = await axios.delete(API_DATASETS_DIMENSION_BY_ID(id));

      if (result.status === 204) {
        const dimension = dimensions.filter((dim) => dim.id === id)[0];
        setDimensions((prev) => prev.filter((dim) => dim.id !== id));
        setDimensionsLength(dimensions.length - 1);
        display(
          t("datasetsCentre.common.success"),
          t("datasetsCentre.datasets.dimensions.deleteSuccess", {
            code: dimension.code,
          }),
        );
      } else {
        console.error(result.data);
        displayError(result.data);
      }
    } catch (error) {
      console.error(error);
      displayUnexpectedError();
    } finally {
      setIsDeleting(false);
      setDimensionToDelete(null);
    }
  };

  const filteredDimensions = dimensions.filter((dimension) => {
    const searchTerm = dimensionFilter.toLowerCase();
    return (
      dimension.code.toLowerCase().includes(searchTerm) ||
      dimension.name.en.toLowerCase().includes(searchTerm) ||
      dimension.name.es.toLowerCase().includes(searchTerm)
    );
  });

  useEffect(() => {
    const loadDatasetDimensions = async () => {
      try {
        const response = await axios.get(API_DATASETS_DIMENSIONS(id));
        const data = response.data;
        setDimensions(data);
        setDimensionsLength(data.length);
      } catch (error) {
        const data = error?.response?.data;
        console.error(data);
        displayUnexpectedError();
      }
    };

    const loadDimensionTypes = async () => {
      try {
        const response = await axios.get(API_DATASETS_DIMENSION_TYPES);
        const data = response.data;
        setDimensionTypes(data);
      } catch (error) {
        const data = error?.response?.data;
        console.error(data);
        displayUnexpectedError();
      }
    };

    loadDatasetDimensions();
    loadDimensionTypes();
  }, [id]);

  return (
    <>
      <AddDimensionDialog
        datasetId={id}
        dimensionTypes={dimensionTypes}
        isDimensionDialogOpen={isDimensionDialogOpen}
        setIsDimensionDialogOpen={setIsDimensionDialogOpen}
        onAddDimensionSuccessfully={onAddDimensionSuccessfully}
        checkDimensionCodeDuplicated={checkDimensionCodeDuplicated}
        checkDimensionColumnNameDuplicated={checkDimensionColumnNameDuplicated}
      />
      <DeleteDimensionDialog
        dimension={dimensionToDelete}
        onCancel={cancelDeleteDimension}
        isDeleting={isDeleting}
        onDeleteHandler={deleteDimension}
      />
      <TabsContent value="dimensions" className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold">
              {t("datasetsCentre.datasets.dimensions.title")}
            </h2>
            <p className="text-muted-foreground">
              {t("datasetsCentre.datasets.dimensions.description")}
            </p>
          </div>
          <div className="flex items-center gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
              <Input
                placeholder={t("datasetsCentre.common.filterPlaceholder")}
                value={dimensionFilter}
                onChange={(e) => setDimensionFilter(e.target.value)}
                className="pl-10 w-64"
              />
            </div>
            <Button
              onClick={openDimensionDialog}
              className="gap-2"
              disabled={editingId !== null}
            >
              <Plus className="w-4 h-4" />
              {t("datasetsCentre.datasets.dimensions.addButton")}
            </Button>
          </div>
        </div>

        {dimensionFilter && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <span>
              {t("datasetsCentre.datasets.dimensions.showingCount", {
                count: filteredDimensions.length,
                total: dimensions.length,
              })}
            </span>
            {filteredDimensions.length !== dimensions.length && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setDimensionFilter("")}
                className="h-auto p-1 text-xs"
              >
                {t("datasetsCentre.common.clearFilter")}
              </Button>
            )}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {filteredDimensions.map((dimension, index) => {
            const isEditing = editingId === dimension.id;
            const displayData = isEditing ? editingData : dimension;

            return (
              <Card
                key={dimension.id}
                className={isEditing ? "ring-2 ring-primary" : ""}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg">
                      {displayData.code || `Dimension #${index + 1}`}
                    </CardTitle>
                    <div className="flex items-center gap-2">
                      {isEditing ? (
                        <>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={saveEditingDimension}
                            disabled={isSubmitting}
                            className="text-green-600 hover:text-green-700"
                          >
                            {isSubmitting ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <CheckCircle className="w-4 h-4" />
                            )}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={cancelEditingDimension}
                            disabled={isSubmitting}
                            className="text-gray-600 hover:text-gray-700"
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </>
                      ) : (
                        <>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => startEditingDimension(dimension)}
                            disabled={editingId !== null}
                            className="text-blue-600 hover:text-blue-700"
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() =>
                              displayDeleteDimensionDialog(dimension)
                            }
                            disabled={editingId !== null}
                            className="text-destructive hover:text-destructive"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  {isEditing && unexpectedError && <UnexpectedBackendError />}
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                    <div className="space-y-2">
                      <Label>{t("datasetsCentre.common.code")} *</Label>
                      {isEditing ? (
                        <>
                          <Input
                            value={displayData.code}
                            onChange={(e) =>
                              setEditingData((prev) =>
                                prev ? { ...prev, code: e.target.value } : null,
                              )
                            }
                            placeholder="dimension_code"
                            className={
                              formErrors.code ? "border-destructive" : ""
                            }
                          />
                          {formErrors.code && (
                            <p className="text-sm text-destructive">
                              {formErrors.code}
                            </p>
                          )}
                        </>
                      ) : (
                        <div className="px-3 py-2 bg-muted rounded-md text-sm">
                          {displayData.code}
                        </div>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label>{t("datasetsCentre.common.columnName")} *</Label>
                      {isEditing ? (
                        <>
                          <Input
                            value={displayData.columnName}
                            onChange={(e) =>
                              setEditingData((prev) =>
                                prev
                                  ? { ...prev, columnName: e.target.value }
                                  : null,
                              )
                            }
                            placeholder="column_name"
                            className={
                              formErrors.columnName ? "border-destructive" : ""
                            }
                          />
                          {formErrors.columnName && (
                            <p className="text-sm text-destructive">
                              {formErrors.columnName}
                            </p>
                          )}
                        </>
                      ) : (
                        <div className="px-3 py-2 bg-muted rounded-md text-sm">
                          {displayData.columnName}
                        </div>
                      )}
                    </div>
                    <div className="space-y-2 md:col-span-2 xl:col-span-1">
                      <Label>{t("datasetsCentre.common.dataType")} *</Label>
                      {isEditing ? (
                        <OptionsDropdown
                          value={displayData.dataType}
                          onValueChange={(value) =>
                            setEditingData((prev) =>
                              prev
                                ? {
                                    ...prev,
                                    dataType: value,
                                  }
                                : null,
                            )
                          }
                          items={dimensionTypes}
                        />
                      ) : (
                        <div className="px-3 py-2 bg-muted rounded-md text-sm capitalize">
                          {
                            dimensionTypes.filter(
                              (dt) => dt.code === displayData.dataType,
                            )[0].name
                          }
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label>{t("datasetsCentre.common.nameEn")} *</Label>
                      {isEditing ? (
                        <>
                          <Input
                            value={displayData.name.en}
                            onChange={(e) =>
                              setEditingData((prev) =>
                                prev
                                  ? {
                                      ...prev,
                                      name: {
                                        ...prev.name,
                                        en: e.target.value,
                                      },
                                    }
                                  : null,
                              )
                            }
                            placeholder={t(
                              "datasetsCentre.datasets.dimensions.addNameEnPlaceholder",
                            )}
                            className={
                              formErrors.nameEn ? "border-destructive" : ""
                            }
                          />
                          {formErrors.nameEn && (
                            <p className="text-sm text-destructive">
                              {formErrors.nameEn}
                            </p>
                          )}
                        </>
                      ) : (
                        <div className="px-3 py-2 bg-muted rounded-md text-sm">
                          {displayData.name.en}
                        </div>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label>{t("datasetsCentre.common.nameEs")} *</Label>
                      {isEditing ? (
                        <>
                          <Input
                            value={displayData.name.es}
                            onChange={(e) =>
                              setEditingData((prev) =>
                                prev
                                  ? {
                                      ...prev,
                                      name: {
                                        ...prev.name,
                                        es: e.target.value,
                                      },
                                    }
                                  : null,
                              )
                            }
                            placeholder={t(
                              "datasetsCentre.datasets.dimensions.addNameEsPlaceholder",
                            )}
                            className={
                              formErrors.nameEs ? "border-destructive" : ""
                            }
                          />
                          {formErrors.nameEs && (
                            <p className="text-sm text-destructive">
                              {formErrors.nameEs}
                            </p>
                          )}
                        </>
                      ) : (
                        <div className="px-3 py-2 bg-muted rounded-md text-sm">
                          {displayData.name.es}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label>
                        {t("datasetsCentre.common.descriptionEn")} *
                      </Label>
                      {isEditing ? (
                        <Textarea
                          value={displayData.description.en}
                          onChange={(e) =>
                            setEditingData((prev) =>
                              prev
                                ? {
                                    ...prev,
                                    description: {
                                      ...prev.description,
                                      en: e.target.value,
                                    },
                                  }
                                : null,
                            )
                          }
                          placeholder={t(
                            "datasetsCentre.datasets.dimensions.addDescriptionEnPlaceholder",
                          )}
                          rows={2}
                        />
                      ) : (
                        <div className="px-3 py-2 bg-muted rounded-md text-sm min-h-[60px]">
                          {displayData.description.en}
                        </div>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label>
                        {t("datasetsCentre.common.descriptionEs")} *
                      </Label>
                      {isEditing ? (
                        <Textarea
                          value={displayData.description.es}
                          onChange={(e) =>
                            setEditingData((prev) =>
                              prev
                                ? {
                                    ...prev,
                                    description: {
                                      ...prev.description,
                                      es: e.target.value,
                                    },
                                  }
                                : null,
                            )
                          }
                          placeholder={t(
                            "datasetsCentre.datasets.dimensions.addDescriptionEsPlaceholder",
                          )}
                          rows={2}
                        />
                      ) : (
                        <div className="px-3 py-2 bg-muted rounded-md text-sm min-h-[60px]">
                          {displayData.description.es}
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {filteredDimensions.length === 0 && dimensionFilter && (
          <Card className="border-dashed">
            <CardContent className="flex flex-col items-center justify-center py-12">
              <p className="text-muted-foreground mb-4">
                {t("datasetsCentre.datasets.dimensions.filterZeroResults")}
              </p>
              <Button
                onClick={() => setDimensionFilter("")}
                variant="outline"
                className="gap-2 bg-transparent"
              >
                {t("datasetsCentre.common.clearFilter")}
              </Button>
            </CardContent>
          </Card>
        )}

        {dimensions.length === 0 && (
          <Card className="border-dashed">
            <CardContent className="flex flex-col items-center justify-center py-12">
              <p className="text-muted-foreground mb-4">
                {t("datasetsCentre.datasets.dimensions.noDimensions")}
              </p>
              <Button
                onClick={openDimensionDialog}
                variant="outline"
                className="gap-2 bg-transparent"
              >
                <Plus className="w-4 h-4" />
                {t("datasetsCentre.datasets.dimensions.addFirstDimension")}
              </Button>
            </CardContent>
          </Card>
        )}
      </TabsContent>
    </>
  );
};

export default DimensionsTabContent;
